import { signupUser } from 'api/v1/signup';
import Joi from 'joi';
import { pick, type Dictionary } from 'lodash';
import type { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Nullable } from 'types/global.types';
import type { CompanyInfo, Enterprise, SignupData } from 'types/signup.types';
import type { IUser } from 'types/users.types';
import {
  AvailableCountries,
  AvailableLanguages,
  AvailableLanguagesBE,
  AvailableLanguagesDE,
  VATTypeEnum,
} from 'types/users.types';
import { getCookie } from 'utils/helpers';

export enum StepsIds {
  Email = 'Email',
  FirstName = 'FirstName',
  LastName = 'LastName',
  SelectCountry = 'SelectCountry',
  AccountType = 'AccountType',
  VATType = 'VATType',
  VATFrequency = 'VATFrequency',
  Promocode = 'Promocode',
  Password = 'Password',
  Enterprise = 'Enterprise',
  VATNumber = 'VATNumber',
  NaturalType = 'naturalType',
  /**
   * Not currently used (per Asana); retained for possible future use.
   * @see https://app.asana.com/1/1170968071291608/project/1205966499941736/task/1213375925638250
   */
  ComplementaryDays = 'ComplementaryDays',
  CompanyName = 'CompanyName',
  Tenure = 'Tenure',
  Profession = 'Profession',
  AreYouSelfEmployed = 'AreYouSelfEmployed',
  BecomingSelfEmployed = 'BecomingSelfEmployed',
  LostNonSelfEmployed = 'LostNonSelfEmployed',
  UnsureToBecomeSeleftEmployed = 'UnsureToBecomeSeleftEmployed',
  ToBeSEAccountType = 'ToBeSEAccountType',
  ToBeSEOccupation = 'ToBeSEOccupation',
  UnsureAccountType = 'UnsureAccountType',
  UnsureOccupation = 'UnsureOccupation',
  UnsureVATType = 'UnsureVATType',
  DefaultAccountType = 'DefaultAccountType',
  DefaultOccupation = 'DefaultOccupation',
  DefaultVATType = 'DefaultVATType',
  HowLongBeenSE = 'HowLongBeenSE',
  HearAboutUsQuestions = 'HearAboutUsQuestions',
  SignUpIntent = 'SignUpIntent',
  SuggestPeppol = 'SuggestPeppol',
  Street = 'Street',
  PostalCode = 'PostalCode',
  City = 'City',
}

export enum FieldsNames {
  email = 'email',
  firstName = 'firstName',
  lastName = 'lastName',
  type = 'accountType',
  VATType = 'VATType',
  VATFrequency = 'VATReturnFrequency',
  companyInfo = 'companyInfo',
  VATNumber = 'VATNumber',
  country = 'country',
  addressCountry = 'address.country',
  promocode = 'promocode',
  password = 'password',
  occupation = 'occupation',
  naturalType = 'naturalType',
  complementaryDays = 'complementaryDays',
  companyName = 'companyName',
  tenure = 'tenure',
  APC1 = 'APC1',
  APC2 = 'APC2',
  intercomLeadId = 'intercomLeadId',
  selfEmployed = 'selfEmployed',
  thinksOfBecomingSelfEmployed = 'thinksOfBecomingSelfEmployed',
  howLongAsSE = 'howLongAsSE',
  googleIdToken = 'googleIdToken',
  appleIdToken = 'appleIdToken',
  hearAboutUs = 'hearAboutUs',
  signed_up_for = 'signed_up_for',
  createPeppol = 'createPeppol',
  street = 'street',
  zip = 'zipcode',
  city = 'city',
}

export enum ActionsIds {
  // shared
  SubmitEmail = 'SubmitEmail',
  SubmitSSO = 'SubmitSSO',
  SubmitSSO_LastNameMissing = 'SubmitSSO_LastNameMissing',
  SubmitFirstName = 'SubmitFirstName',
  SubmitLastName = 'SubmitLastName',
  SelectedCountryDE = 'SelectedCountryDE',
  SelectedCountryBE = 'SelectedCountryBE',
  SubmitPromocode = 'SubmitPromocode',
  SubmitPassword = 'SubmitPassword',
  SubmitProfession = 'SubmitProfession',
  YesImSE = 'YesImSE',
  NotSE = 'NotSE',
  NotSureSE = 'NotSureSE',
  SelectWantToBecomeSE = 'SelectWantToBecomeSE',
  SelectDontWantToBecomeSE = 'SelectDontWantToBecomeSE',
  SelectNotSureToBecomeSE = 'SelectNotSureToBecomeSE',
  NotSESelectedAccountType = 'NotSESelectedAccountType',
  SelectedOccupation = 'SelectedOccupation',
  ContinueUnsureFlow = 'ContinueUnsureFlow',
  NotSureOfAccountType = 'NotSureOfAccountType',
  NotSureOfOccupation = 'NotSureOfOccupation',
  NotSureOfVATType = 'NotSureOfVATType',
  NotSureOfVATFrequency = 'NotSureOfVATFrequency',
  ClickFinishWithDefaultAccountType = 'ClickFinishWithDefaultAccountType',
  ClickFinishWithDefaultOccupation = 'ClickFinishWithDefaultOccupation',
  ClickFinishWithDefaultVATType = 'ClickFinishWithDefaultVATType',
  ClickFinishWithDefaultVATFrequency = 'ClickFinishWithDefaultVATFrequency',
  ClickContinueSignupProcess = 'ClickContinueSignupProcess',
  SelectHowLongAsSE = 'SelectHowLongAsSE',
  // DE
  SelectedAccountType = 'SelectedAccountType',
  SelectedVATTypeSubject = 'SelectedVATTypeSubject',
  SelectedVATTypeOther = 'SelectedVATTypeOther',
  SelectedVATFrequency = 'SelectedVATFrequency',
  SubmitTenure = 'SubmitTenure',
  SubmitHearAboutUsQuestions = 'SubmitHearAboutUsQuestions',
  SubmitSignUpIntent = 'SubmitSignUpIntent',

  // BE
  SelectedAccountTypeLegal = 'SelectedAccountTypeLegal',
  SelectedAccountTypeOther = 'SelectedAccountTypeOther',
  SubmitNaturalTypeAsPrincipal = 'SubmitOccupationAsPrincipal',
  SubmitNaturalTypeAsComplementary = 'SubmitOccupationAsComplementary',
  SelectedVATType = 'SelectedVATType',
  SubmitComplementaryDays = 'SubmitComplementaryDays',
  SubmitVATNumber = 'SubmitVATNumber',
  SelectEnterManualVATNumber = 'SubmitManualVATNumber',
  SubmitCompanyName = 'SubmitCompanyName',
  SubmitStreet = 'SubmitStreet',
  SubmitZip = 'SubmitZip',
  SubmitCity = 'SubmitCity',
  SelectCompanyInfo = 'SelectCompanyInfo',
  SelectedCreatePeppol = 'SelectedCreatePeppol',
  SelectedSkipPeppol = 'SelectedSkipPeppol',
  BusinessAddressMissingStreet = 'BusinessAddressMissingStreet',
  BusinessAddressMissingZip = 'BusinessAddressMissingZip',
  BusinessAddressMissingCity = 'BusinessAddressMissingCity',
  BusinessAddressMissingStreet_Zip_City = 'BusinessAddressMissingStreet_Zip_City',
  BusinessAddressMissingStreet_Zip = 'BusinessAddressMissingStreet_Zip',
  BusinessAddressMissingStreet_City = 'BusinessAddressMissingStreet_City',
  BusinessAddressMissingZip_City = 'BusinessAddressMissingZip_City',
  BusinessAddressComplete = 'BusinessAddressComplete',
}

export enum NaturalTypeEnum {
  principal = 'principal',
  complementary = 'complementary',
}

export enum AccountTypeShortEnum {
  one_person_company = 'one_person_company',
  natural_person = 'natural_person',
  natural_person_student = 'natural_person_student',
  freiberufler = 'freiberufler',
  gewerbetreibender = 'gewerbetreibender',
}

export enum TenureTypesEnum {
  less_than_a_year = 'less_than_a_year',
  more_than_a_year = 'more_than_a_year',
}

export enum HowLongAsSEEnum {
  less_than_a_year = 'less_than_a_year',
  more_than_a_year = 'more_than_a_year',
  unknown = 'unknown',
}

export type Data = Dictionary<any>;

// Type for form values extracted from FieldsNames
export type FormValues = {
  // Basic user info
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;

  // Account and business info
  accountType?: string;
  VATType?: string;
  VATReturnFrequency?: string;
  VATNumber?: string;
  companyName?: string;
  occupation?: string;

  // Country and language
  country?: AvailableCountries;

  // Belgium-specific fields
  naturalType?: NaturalTypeEnum;
  complementaryDays?: number;
  companyInfo?: CompanyInfo;

  // Germany-specific fields
  tenure?: TenureTypesEnum;

  // Self-employment info
  selfEmployed?: string;
  thinksOfBecomingSelfEmployed?: string;
  howLongAsSE?: HowLongAsSEEnum;

  // Promotional and tracking
  promocode?: string;
  intercomLeadId?: string;
  hearAboutUs?: string;
  signed_up_for?: string[];

  // SSO tokens
  googleIdToken?: string;
  appleIdToken?: string;

  // Additional fields from FieldsNames
  APC1?: string;
  APC2?: string;

  street?: string;
  zipcode?: string;
  city?: string;
  createPeppol?: boolean;
};

export type ScreenUIProps = {
  data?: Data;
  allData?: FormValues;
  onAction: (actionId: ActionsIds, screenData?: Data) => Promise<any> | void;
};
export type ScreenUI = {
  MainComponent: FC<ScreenUIProps>;
  SideComponent: FC<ScreenUIProps>;
};

export type PreStep = {
  id: StepsIds;
  uid?: string;
  screen: ScreenUI;
  next?: {
    [key in ActionsIds]?: PreStep;
  };
  // data?: Data;
};

export type Step = Omit<PreStep, 'next' | 'uid'> & {
  uid: string;
  next?: {
    [key in ActionsIds]?: Step;
  };
  key?: string;
  prev?: Step;
};

type Route = { uid: string; id: string }[];

export type Flow = {
  entry: Step;
  routes: Route[];
  defaultValues?: Data;
};

export type StepData = {
  uid: string;
  id: StepsIds;
  data?: Data;
  key?: string;
};

export const mainScreenUIAnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const headerUIAnimationProps = mainScreenUIAnimationProps;

export const sideScreenUIAnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: { delay: 0.25 } },
};

export const stepsBarAnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const combineStepsData = (stepsData: StepData[], init: Data = {}) => {
  return stepsData.reduce(
    (acc, stepData) => ({ ...acc, ...stepData.data }),
    init as Data,
  ) as FormValues;
};

export const adaptSteps = (
  (uid: number) =>
  (preStep: PreStep): Step => {
    const step = { ...preStep, uid: preStep.uid ?? `_${uid++}_` } as Step;
    if (!step.next) return step;
    step.next = Object.keys(step.next).reduce((acc, key) => {
      const child = adaptSteps(step.next![key as ActionsIds] as PreStep);
      child.prev = step;
      acc![key as ActionsIds] = child;
      return acc;
    }, {} as Step['next']);
    return step;
  }
)(1);

export const getRoutes = (step: Step, base: Route = []): Route[] => {
  const stepMeta = { uid: step.uid, id: step.id, key: step.key };
  if (!step.next) return [base.concat(stepMeta)];
  return Object.keys(step.next).flatMap((key) =>
    getRoutes(step.next![key as ActionsIds] as Step, base.concat(stepMeta)),
  );
};

const normalizeVATNumber = (str?: string) => {
  return str?.replace(/\s+/g, '').toUpperCase();
};

const normalizeEmail = (str?: string) => {
  if (!str) return;
  return str.toLowerCase();
};

const buildAccountTypeBE = (values: FormValues) => {
  if (values.accountType === AccountTypeShortEnum.natural_person) {
    if (!values.naturalType) return;
    if (values.naturalType === NaturalTypeEnum.principal) {
      if (!values.VATType) return;
      if (values.VATType === VATTypeEnum.subjectToVAT) {
        return `${values.accountType}_${values.naturalType}_vat`;
      }
      return `${values.accountType}_${values.naturalType}_no_vat`;
    }
    if (values.naturalType === NaturalTypeEnum.complementary) {
      return `${values.accountType}_${values.naturalType}`;
    }
  }

  if (values.accountType === AccountTypeShortEnum.natural_person_student) {
    if (!values.VATType) return;
    if (values.VATType === VATTypeEnum.subjectToVAT) {
      return `${values.accountType}_vat`;
    }
    return `${values.accountType}_no_vat`;
  }

  if (values.accountType === AccountTypeShortEnum.one_person_company) {
    return values.accountType;
  }
};

const buildAccountTypeDE = (values: FormValues) => {
  const naturalType = NaturalTypeEnum.principal; // no screen for it, hardcoded for now

  if (values.accountType === AccountTypeShortEnum.gewerbetreibender) {
    return `${values.accountType}_${naturalType}`;
  }

  if (values.accountType === AccountTypeShortEnum.freiberufler) {
    if (!values.VATType) return;
    if (values.VATType === VATTypeEnum.subjectToVAT) {
      return `${values.accountType}_${naturalType}_vat`;
    }
    return `${values.accountType}_${naturalType}_no_vat`;
  }
};

export type FormDataArgs = {
  values: FormValues;
  language?: number;
  urlParams?: { [x: string]: unknown };
};

const formatFormValues = (data: FormDataArgs) => {
  const {
    companyInfo,
    companyName,
    VATNumber,
    email,
    password,
    firstName,
    lastName,
    promocode,
    country,
    complementaryDays,
    VATType,
    occupation,
    APC1,
    APC2,
    googleIdToken,
    appleIdToken,
    selfEmployed,
    thinksOfBecomingSelfEmployed,
    howLongAsSE,
    VATReturnFrequency,
    naturalType,
    accountType: formAccountType,
    street,
    zipcode,
    city,
    signed_up_for,
  } = data.values;

  const isCountryBE = country === AvailableCountries.belgium;
  const isCountryDE = country === AvailableCountries.germany;

  const common = {
    email: normalizeEmail(email),
    firstName,
    lastName,
    country,
    promocode,
    password,
    occupation,
    APC1,
    APC2,
    language: data.language,
    googleIdToken,
    appleIdToken,
    isNotYetSelfEmployed: selfEmployed ? selfEmployed !== 'yes' : undefined,
    selfEmployed,
    thinksOfBecomingSelfEmployed,
    howLongAsSE,
    cello_ucc: data.urlParams?.ucc || getCookie('cello-referral') || undefined,
    financeAds_s_id: data.urlParams?.s_id || getCookie('s_id') || undefined,
    signed_up_for,
  };

  if (isCountryDE) {
    return {
      ...common,
      accountType: buildAccountTypeDE(data.values),
      VATType,
      VATReturnFrequency,
      address: {
        street: null,
        city: null,
        zip: null,
        country: 'Germany',
      },
    };
  }

  if (isCountryBE) {
    return {
      ...common,
      accountType: buildAccountTypeBE(data.values),
      complementaryDays:
        (formAccountType === AccountTypeShortEnum.natural_person_student &&
          (!naturalType || naturalType === NaturalTypeEnum.principal)) ||
        (naturalType === NaturalTypeEnum.complementary && !complementaryDays)
          ? 5
          : complementaryDays,
      VATType:
        formAccountType === AccountTypeShortEnum.one_person_company
          ? VATTypeEnum.subjectToVAT
          : VATType,
      VATNumber: normalizeVATNumber(companyInfo?.VATNumber || VATNumber),
      address: {
        street: street || companyInfo?.street || null,
        city: city || companyInfo?.city || null,
        zip: zipcode || companyInfo?.zipcode || null,
        country: 'Belgium',
      },
      NACECodes: companyInfo?.NACECodes,
      startDate: companyInfo?.startDate,
      companyName: companyInfo?.name || companyName,
      peppolStatus: data.values.createPeppol ? 'started_on_signup' : undefined,
    };
  }

  return common;
};

export const onSubmit = (data: FormDataArgs) => {
  const { howLongAsSE, ...rest } = formatFormValues(data);
  return signupUser({
    ...(rest as SignupData),
    self_employed_tenure_on_signup: howLongAsSE,
    ...(data.urlParams?.invitation_token
      ? { invitation_token: data.urlParams.invitation_token as string }
      : {}),
  });
};

export const formatFormDataForTracking = (data: FormDataArgs) => {
  const values = formatFormValues(data);
  // whitelist data for tracking
  return {
    ...pick(values, [
      FieldsNames.email,
      FieldsNames.firstName,
      FieldsNames.lastName,
      FieldsNames.country,
      FieldsNames.promocode,
      FieldsNames.occupation,
      FieldsNames.APC1,
      FieldsNames.APC2,
      FieldsNames.type,
      FieldsNames.VATType,
      FieldsNames.VATFrequency,
      FieldsNames.complementaryDays,
      FieldsNames.VATNumber,
      FieldsNames.companyName,
      'language',
      'cello_ucc',
      'financeAds_s_id',
      'address',
      'NACECodes',
      'startDate',
      'isNotYetSelfEmployed',
    ]),
    is_already_self_employed: values.selfEmployed, // yes no unknown
    how_long_as_self_employed: values.howLongAsSE, // less_than_a_year more_than_a_year unknown
    thinking_of_becoming_self_employed: values.thinksOfBecomingSelfEmployed, // yes no not_sure
  };
};

export const formatCreatedUserForAmplitude = (
  user: IUser,
  language: string,
  stepsData: Data,
) => ({
  user_country: user.country,
  VAT_type: user.VATType,
  account_type: user.accountType,
  phoneNumber: user.phoneNumber,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  language: language,
  VATNumber: user.VATNumber,
  companyName: user.companyName,
  APC1: user.APC1,
  APC2: user.APC2,
  is_registered_as_self_employed: stepsData?.howLongAsSE,
  thinking_of_becoming_self_employed: stepsData?.thinksOfBecomingSelfEmployed,
  ...(Array.isArray(user.NACECodes?.main)
    ? {
        NACE_main: user.NACECodes?.main.toString(),
      }
    : null),
  ...(Array.isArray(user.NACECodes?.secondary)
    ? {
        NACE_secondary: user.NACECodes?.secondary.toString(),
      }
    : null),
  regstration_date: user.created,
  ...(user.startDate
    ? {
        KBO_start_date: new Date(user.startDate).getTime() / 1000,
      }
    : null),
  ...user.address,
});
// export const clientId =
//   '20015120266-80cvhlhqkb4tk5hq16kb4h32ei79rhq9.apps.googleusercontent.com';

export const renderBody = (text: string, showIntercom?: () => void) => {
  return text.split(/\n\n+/).map((t, i) => (
    <ReactMarkdown
      key={i}
      components={{
        p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
        li: ({ children }) => (
          <li className="whitespace-pre-wrap">&bull; {children}</li>
        ),
        em: ({ children }) => (
          <button
            className="text-taxes-500 underline font-semibold"
            onClick={showIntercom}
          >
            {children}
          </button>
        ),
        a: ({ children, href }) => (
          <a
            href={href}
            className="text-taxes-500 underline"
            target="_blank"
            rel="noreferrer"
          >
            {children}
          </a>
        ),
      }}
    >
      {t}
    </ReactMarkdown>
  ));
};

export const getAllowedLanguages = (
  _country?: AvailableCountries,
): string[] => {
  if (_country === AvailableCountries.belgium) {
    return Object.values(AvailableLanguagesBE);
  }
  if (_country === AvailableCountries.germany) {
    return Object.values(AvailableLanguagesDE);
  }
  return Object.values(AvailableLanguages);
};

export const getCountryFromLanguage = (_lang: AvailableLanguages) => {
  if ([AvailableLanguages.fr, AvailableLanguages.nl].includes(_lang)) {
    return AvailableCountries.belgium;
  }
  if ([AvailableLanguages.de].includes(_lang)) {
    return AvailableCountries.germany;
  }
};

export type StepSkipMeta = { data: Data; next: ActionsIds };

export const countryStepKey = StepsIds.SelectCountry;

export const getCountryFromData = (
  data?: Data,
): AvailableCountries | undefined => {
  return data?.[FieldsNames.country];
};

export const getCountryStepSkipMeta = (
  _lang: AvailableLanguages,
): StepSkipMeta | undefined => {
  if ([AvailableLanguages.fr, AvailableLanguages.nl].includes(_lang)) {
    return {
      data: { [FieldsNames.country]: AvailableCountries.belgium },
      next: ActionsIds.SelectedCountryBE,
    };
  }
  if (AvailableLanguages.de === _lang) {
    return {
      data: { [FieldsNames.country]: AvailableCountries.germany },
      next: ActionsIds.SelectedCountryDE,
    };
  }
};

export const shouldSkipCountryStep = (_lang: AvailableLanguages) => {
  return (
    [AvailableLanguages.fr, AvailableLanguages.nl].includes(_lang) ||
    AvailableLanguages.de === _lang
  );
};

export const isCountryStep = (key?: string) => key === countryStepKey;

export const findStepByUID = (uid: string, step: Step): Step | undefined => {
  if (step.uid === uid) return step;
  if (step.next) {
    const steps = Object.values(step.next);
    let i = 0;
    while (i <= steps.length - 1) {
      const result = findStepByUID(uid, steps[i]);
      if (result) return result;
      i += 1;
    }
  }
};

export const addressValidationsBE = {
  street: Joi.string()
    .trim()
    .pattern(/(?=.*\d)(?=.*\D)/)
    .required(),
  city: Joi.string().trim().min(2).required(),
  zip: Joi.string()
    .trim()
    .pattern(/^\d{4}$/)
    .required(),
};

export const getBusinessAddressNextActionKey = (
  enterprise: Nullable<Pick<Enterprise, 'street' | 'city' | 'zipcode'>>,
) => {
  if (!enterprise) return ActionsIds.BusinessAddressMissingStreet_Zip_City;

  const street = addressValidationsBE.street.validate(enterprise.street);
  const city = addressValidationsBE.city.validate(enterprise.city);
  const zip = addressValidationsBE.zip.validate(enterprise.zipcode);

  if (street.error && city.error && zip.error) {
    return ActionsIds.BusinessAddressMissingStreet_Zip_City;
  }

  if (street.error && city.error) {
    return ActionsIds.BusinessAddressMissingStreet_City;
  }

  if (street.error && zip.error) {
    return ActionsIds.BusinessAddressMissingStreet_Zip;
  }

  if (city.error && zip.error) {
    return ActionsIds.BusinessAddressMissingZip_City;
  }

  if (street.error) {
    return ActionsIds.BusinessAddressMissingStreet;
  }

  if (city.error) {
    return ActionsIds.BusinessAddressMissingCity;
  }

  if (zip.error) {
    return ActionsIds.BusinessAddressMissingZip;
  }

  return ActionsIds.BusinessAddressComplete;
};
